import axios, { AxiosError, AxiosInstance, AxiosResponse } from "axios";
import { LocalStorage } from "../commons/LocalStorage";
import { MENSAGEM_FILTRO_SEGMENTO_CONTA_DISTRIBUIDOR, MENSAGEM_FILTRO_SEGMENTO_CONTA_REVENDA, MENSAGEM_VIGENCIA_SUBSTITUICAO_CONTA_ENCERRADO } from "src/App";
import { appStore } from "../store/AppStore";
import { LoginStore } from "../store/LoginStore";
import { notifierStore } from "../store/NotifierStore";
import { isLoading$, noPropagateDialog$, tokenExpirado$ } from "../store/ApiEvents";

export enum STATUS_CODE {
  NO_CONTENT = 204,
  OK = 200,
  CREATED = 201,
  BAD_REQUEST = 400,
  UNAUTHORIZED = 401,
  TOO_MANY_REQUESTS = 429,
  INTERNAL_SERVER_ERROR = 500,
}

export interface IDataRequest {
  url: string;
  data?: object;
  headers?: object;
  withCredentials?: boolean;
  guarded?: boolean;
  onUploadProgress?: (progressEvent: any) => void
}

export interface IDataResponse<T = any> {
  data: T;
  statusCode: number;
  message: string;
  headers?: Record<string,string>;
}

export interface IHttpClient<T = any> {
  api: T;

  get(data: IDataRequest): Promise<IDataResponse<T>>;
  post(data: IDataRequest): Promise<IDataResponse<T>>;
  put(data: IDataRequest): Promise<IDataResponse<T>>;
  delete(data: IDataRequest): Promise<IDataResponse<T>>;

  getFile(data: IDataRequest): Promise<IDataResponse>;
}

const formatMessages = [
  {
    receivedMessage: 'CEP não cadastrato!;',
    formattedMessage: 'CEP não cadastrado. Preencha os campos do endereço manualmente.'
  }
]

class Api implements IHttpClient<AxiosInstance> {
  api: AxiosInstance;

  constructor(baseUrl: string, readonly noLoading: boolean = false, noMessageError?: boolean) {
    this.api = axios.create({
      baseURL: baseUrl,
      withCredentials: false,
    });

    this.api.interceptors.request.use((request) => {
      if (!this.noLoading) {
        isLoading$.next(true);
      }

      if (LoginStore.isLoggedIn()) {
        request.headers.Authorization = request.headers.Authorization || `Bearer ${LocalStorage.get("solar.token")}`;
      }
      return request;
    });

    this.api.interceptors.response.use(
      (response) => {
        if (response.headers.newtoken) {
          LocalStorage.set("solar.token", response.headers.newtoken)
        }
        isLoading$.next(false);
        return response;
      },
      async (error: AxiosError<any>) => {
        isLoading$.next(false);
        if (error?.response?.status === STATUS_CODE.UNAUTHORIZED) {
          tokenExpirado$.next(true);
          notifierStore.setNotifier({
            timeOut: 0,
            title: "Aviso!",
            content: "Seu acesso ao Portal de Projetos expirou. Efetue o login novamente.",
            onClose: async () => {
              notifierStore.setOpen(false)
              const contaUrl = await LoginStore.getContaUrlCompleto(window.location.origin)
              if (contaUrl) {
                LoginStore.doLogOut();
                window.location.href = contaUrl
              }
            },
          })
          notifierStore.setOpen(true)
          noPropagateDialog$.next(true)
        } else if (error?.response?.status === STATUS_CODE.TOO_MANY_REQUESTS) {
          notifierStore.setNotifier({
            timeOut: 0,
            title: "Aviso!",
            content: error?.response?.data?.error || "Você excedeu o número de tentativas. Tente novamente em breve.",
            onClose: async () => notifierStore.setOpen(false),
          })
          notifierStore.setOpen(true)
          noPropagateDialog$.next(true)
        } else if (error?.response?.data.message) {
          const formattedMessages = error?.response?.data.message.map((message: string) => {
            const formatMessage = formatMessages.find(formatMessage => formatMessage.receivedMessage === message);
            if (formatMessage) {
              return formatMessage.formattedMessage
            }
            return message;
          })

          if(!noMessageError) {
            appStore.setError([...formattedMessages]);
          }

          if (error.response.headers["conta-post-logout-redirect-url"]) {
            return error
          }

        } /* else {
          appStore.setError([error]);
        } */
      }
    );
  }

  async getFile(data: IDataRequest): Promise<IDataResponse> {
    try {
      let _resp: AxiosResponse = await this.api.get(data.url, {
        headers: data.headers,
        responseType: "arraybuffer",
      });

      if (_resp.status === STATUS_CODE.NO_CONTENT) {
        return {
          data: [],
          message: "Nenhum arquivo retornado",
          statusCode: _resp.status,
        };
      }

      return {
        data: _resp.data,
        message: "Ok",
        statusCode: _resp.status,
      };
    } catch (error) {
      return {
        data: null,
        message: "Error",
        statusCode: STATUS_CODE.INTERNAL_SERVER_ERROR,
      };
    }
  }

  async get(data: IDataRequest): Promise<IDataResponse> {
    try {
      let _resp: AxiosResponse = await this.api.get(data.url, {
        headers: data.headers,
      });

      if (_resp === undefined) {
        return {
          data: [],
          message: "Api está indisponível",
          statusCode: 500,
        };
      }

      if (_resp.status === STATUS_CODE.NO_CONTENT) {
        return {
          data: [],
          message: "Nunhum conteúdo a ser exibido",
          statusCode: _resp.status,
        };
      }

      if (Array.isArray(_resp?.data?.data) && _resp?.data?.data?.includes(MENSAGEM_VIGENCIA_SUBSTITUICAO_CONTA_ENCERRADO)) {
        return {
          data: [],
          message: MENSAGEM_VIGENCIA_SUBSTITUICAO_CONTA_ENCERRADO,
          statusCode: 500,
        };
      }
      
      if (Array.isArray(_resp?.data?.data) && _resp?.data?.data?.includes(MENSAGEM_FILTRO_SEGMENTO_CONTA_REVENDA)) {
        return {
          data: [],
          message: MENSAGEM_FILTRO_SEGMENTO_CONTA_REVENDA,
          statusCode: 500,
        };
      }
      
      if (Array.isArray(_resp?.data?.data) && _resp?.data?.data?.includes(MENSAGEM_FILTRO_SEGMENTO_CONTA_DISTRIBUIDOR)) {
        return {
          data: [],
          message: MENSAGEM_FILTRO_SEGMENTO_CONTA_DISTRIBUIDOR,
          statusCode: 500,
        };
      }

      return {
        data: _resp.data.data,
        message: "Ok",
        statusCode: _resp.status,
      };
    } catch (error) {
      return {
        data: null,
        message: "Error",
        statusCode: STATUS_CODE.INTERNAL_SERVER_ERROR,
      };
    }
  }

  async post(data: IDataRequest): Promise<IDataResponse> {
    try {
      let _resp: AxiosResponse & { response?: any } = await this.api.post(data.url, data.data, {
        headers: data.headers || {},
        onUploadProgress: data?.onUploadProgress
      });

      if (!_resp || _resp?.response?.headers?.["conta-post-logout-redirect-url"]) {
        return {
          data: [],
          message: appStore.getError(),
          statusCode: 400,
          headers: _resp?.response?.headers
        };
      }

      if (_resp.status === STATUS_CODE.NO_CONTENT) {
        return {
          data: [],
          message: "Nunhum conteúdo a ser exibido",
          statusCode: _resp.status,
        };
      }

      return {
        data: _resp.data.data,
        message: _resp?.data?.message || "OK",
        statusCode: _resp.status,
      };
    } catch (error) {
      return {
        data: null,
        message: "Error",
        statusCode: STATUS_CODE.INTERNAL_SERVER_ERROR,
      };
    }
  }

  async patch(data: IDataRequest): Promise<IDataResponse> {
    try {
      let _resp: AxiosResponse = await this.api.patch(data.url, data.data, {
        headers: data.headers || {},
      });
  
      if (_resp.status === STATUS_CODE.NO_CONTENT) {
        return {
          data: [],
          message: "Nunhum conteúdo a ser exibido",
          statusCode: _resp.status,
        };
      }
  
      return {
        data: _resp.data.data,
        message: "Ok",
        statusCode: _resp.status,
      };
    } catch (error) {
      return {
        data: null,
        message: "Error",
        statusCode: STATUS_CODE.INTERNAL_SERVER_ERROR,
      };
    }
  }

  async put(data: IDataRequest): Promise<IDataResponse> {
    try {
      let _resp: AxiosResponse = await this.api.put(data.url, data.data, {
        headers: data.headers || {},
      });

      if (_resp.status === STATUS_CODE.NO_CONTENT) {
        return {
          data: [],
          message: "Nunhum conteúdo a ser exibido",
          statusCode: _resp.status,
        };
      }

      return {
        data: _resp.data.data,
        message: "Ok",
        statusCode: _resp.status,
      };
    } catch (error) {
      return {
        data: null,
        message: "Error",
        statusCode: STATUS_CODE.INTERNAL_SERVER_ERROR,
      };
    }
  }

  async delete(data: IDataRequest): Promise<IDataResponse> {
    try {
      let _resp: AxiosResponse = await this.api.delete(data.url, data.data);

      if (_resp.status === STATUS_CODE.NO_CONTENT) {
        return {
          data: [],
          message: "Nunhum conteúdo a ser exibido",
          statusCode: _resp.status,
        };
      }

      return {
        data: _resp.data.data,
        message: "Ok",
        statusCode: _resp.status,
      };
    } catch (error) {
      return {
        data: null,
        message: "Error",
        statusCode: STATUS_CODE.INTERNAL_SERVER_ERROR,
      };
    }
  }
}

const api = new Api(`${process.env.REACT_APP_API_BASE_URL}`);
const apiNoLoading = new Api(`${process.env.REACT_APP_API_BASE_URL}`, true);

const apiNoMessageError = new Api(`${process.env.REACT_APP_API_BASE_URL}`, false, true);


export default api;
export { apiNoLoading, apiNoMessageError };
